import type { ComponentType, LazyExoticComponent } from "react";
import { lazy } from "react";
import { FolderOpen,
  HardDrive,
  Building2,
  Calendar,
  Mail,
  Video,
  MessageSquare,
  FileText,
  Presentation,
  Table,
  ListTodo,
  Contact,
  Sparkles,
  LayoutGrid,
  Users,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";

export type RouteKind = "component" | "coming-soon";

export interface BaseRoute {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  requiresSuperAdmin?: boolean;
  /**
   * RBAC module scope (#1342), e.g. "chat:access". When set and the org has
   * RBAC enabled, the route is hidden from the sidebar and route-blocked for
   * users whose roles lack the scope. Undefined = never gated.
   */
  moduleScope?: string;
}

export interface ComponentRoute extends BaseRoute {
  kind: "component";
  Component: LazyExoticComponent<ComponentType<unknown>>;
}

export interface ComingSoonRoute extends BaseRoute {
  kind: "coming-soon";
  /** Optional marketing copy beneath the title. */
  blurb?: string;
}

export type Route = ComponentRoute | ComingSoonRoute;


/* ── Primary nav (11 items, exact order) ──────────────────────────────── */
const dashboard: ComponentRoute = {
  id: "dashboard", label: "Intelligence Hub", path: "/", icon: Sparkles, kind: "component",
  Component: lazy(() => import("./modules/assistant/AssistantModule").then((m) => ({ default: m.AssistantModule }))),
};
const planner: ComponentRoute = {
  id: "planner", label: "Planner", path: "/planner", icon: Calendar, kind: "component", moduleScope: "calendar:access",
  Component: lazy(() => import("./modules/planner/PlannerModule").then((m) => ({ default: m.PlannerModule }))),
};
const email: ComponentRoute = {
  id: "email", label: "Email", path: "/email", icon: Mail, kind: "component", moduleScope: "email:access",
  Component: lazy(() => import("./modules/email/EmailModule").then((m) => ({ default: m.EmailModule }))),
};
const call: ComponentRoute = {
  id: "call", label: "Call", path: "/call", icon: Video, kind: "component", moduleScope: "video:access",
  Component: lazy(() => import("./modules/call/CallModule").then((m) => ({ default: m.CallModule }))),
};
const chat: ComponentRoute = {
  id: "chat", label: "Chat", path: "/chat", icon: MessageSquare, kind: "component", moduleScope: "chat:access",
  Component: lazy(() => import("./modules/chat/ChatModule").then((m) => ({ default: m.ChatModule }))),
};
const homework: ComponentRoute = {
  // The virtual workspace (services/office): the floor, presence, time
  // tracking, leave and attendance. `moduleScope` matches the service's own
  // WECO_MODULE_SCOPE. Deliberately absent from ROUTE_APP_IDS until an
  // AppDefinition row exists for it — an unmapped route fails open, whereas a
  // mapped one with no catalog row would be blocked for everybody.
  id: "homework", label: "HomeWork", path: "/homework", icon: Building2, kind: "component", moduleScope: "office:access",
  Component: lazy(() => import("./modules/homework/HomeWorkModule").then((m) => ({ default: m.HomeWorkModule }))),
};
const write: ComponentRoute = {
  id: "write", label: "Write", path: "/write", icon: FileText, kind: "component", moduleScope: "docs:access",
  Component: lazy(() => import("./modules/write/WriteModule").then((m) => ({ default: m.WriteModule }))),
};
const present: ComponentRoute = {
  id: "present", label: "Present", path: "/present", icon: Presentation, kind: "component",
  Component: lazy(() => import("./modules/present/PresentModule").then((m) => ({ default: m.PresentModule }))),
};
const grid: ComponentRoute = {
  id: "grid", label: "Grid", path: "/grid", icon: Table, kind: "component",
  Component: lazy(() => import("./modules/grid/GridModule")),
};
const tasks: ComponentRoute = {
  id: "tasks", label: "Tasks", path: "/tasks", icon: ListTodo, kind: "component",
  Component: lazy(() => import("./modules/tasks/TasksModule").then((m) => ({ default: m.Tasks }))),
};
// Backed by the People service's `my_contacts` app (`/my-contacts/`,
// `/directory/`). Org-toggleable via ROUTE_APP_IDS.contacts → the `contacts`
// AppDefinition row (lib/appAccess.ts).
const contacts: ComponentRoute = {
  id: "contacts", label: "Contacts", path: "/contacts", icon: Contact, kind: "component",
  Component: lazy(() => import("./modules/contacts/ContactsModule").then((m) => ({ default: m.ContactsModule }))),
};

/* ── Workspace section (WeCo extras kept visible + functional) ── */
const people: ComponentRoute = {
  id: "people", label: "Admin", path: "/people", icon: Users, kind: "component",
  requiresSuperAdmin: true,
  Component: lazy(() => import("./modules/admin/AdminModule").then((m) => ({ default: m.AdminModule }))),
};

/* ── Secondary section (design's order) ───────────────────────────────── */
const marketplace: ComponentRoute = {
  id: "marketplace", label: "All apps", path: "/marketplace", icon: LayoutGrid, kind: "component",
  Component: lazy(() => import("./pages/Marketplace").then((m) => ({ default: m.Marketplace }))),
};
const settings: ComponentRoute = {
  id: "settings", label: "Settings", path: "/settings", icon: SettingsIcon, kind: "component",
  Component: lazy(() => import("./pages/Settings").then((m) => ({ default: m.Settings }))),
};

// The old Vault URL. Not in any sidebar section (Files replaced it); kept as a
// route so `/vault/*` links, bookmarks and `getRouteById("vault")` callers
// land on the same place under /files.
const vault: ComponentRoute = {
  id: "vault", label: "Vault", path: "/vault", icon: HardDrive, kind: "component", moduleScope: "drive:access",
  Component: lazy(() => import("./modules/vault/VaultRedirect").then((m) => ({ default: m.VaultRedirect }))),
};

// One Files screen (Files spec §1.1): Drive's UI listing uploads and the live
// Write / Present / Grid documents together. The old `/vault/*` URLs redirect
// here (the `vault` route above, modules/vault/lib/filesPaths.vaultToFilesPath).
const files: ComponentRoute = {
  id: "files", label: "Files", path: "/files", icon: FolderOpen, kind: "component", moduleScope: "drive:access",
  Component: lazy(() => import("./modules/vault/VaultModule").then((m) => ({ default: m.VaultModule }))),
};

export const sidebarPrimary: ReadonlyArray<Route> = [dashboard, planner, email, call, chat, vault, files, homework, write, present, grid, tasks, contacts];
export const sidebarWorkspace: ReadonlyArray<Route> = [people];
export const sidebarSecondary: ReadonlyArray<Route> = [marketplace, settings];

export const routes: ReadonlyArray<Route> = [...sidebarPrimary, ...sidebarWorkspace, ...sidebarSecondary];

/**
 * Sidebar sections — ONE config mirroring the design's TIER_GROUPS
 * (`weco2/data.jsx`, live 20261018a). Inside a group, apps render in the
 * order of `sidebarPrimary` / `sidebarWorkspace` (the design renders
 * `NAV.filter(it => group.apps.includes(it.id))`, i.e. NAV order).
 *
 * The mock's plan-tier switch is not reproduced: what an org can see is
 * decided by the real filters in main.tsx (package / per-org app access +
 * RBAC), and a group whose apps are all hidden does not render. Ids with no
 * registered route are skipped. `labelKey` is the i18n heading.
 */
export interface SidebarSectionConfig {
  id: string;
  labelKey: string;
  apps: ReadonlyArray<string>;
}

export const SIDEBAR_SECTION_CONFIG: ReadonlyArray<SidebarSectionConfig> = [
  { id: "essentials", labelKey: "shell.navGroup.essentials", apps: ["dashboard", "planner", "email", "contacts", "tasks"] },
  { id: "communication", labelKey: "shell.navGroup.communication", apps: ["chat", "call", "homework"] },
  // Design: Write/Present/Grid live inside the Files screen, so they are not
  // in the sidebar. They stay in `routes` (via sidebarPrimary) because their
  // document tabs resolve name + icon from it (the design's DOC_EDITOR_NAV).
  { id: "files", labelKey: "shell.navGroup.files", apps: ["files"] },
  // WeCo's own administration surface (no design counterpart — the design
  // reaches Admin only from Settings; kept on the rail by product decision).
  { id: "workspace", labelKey: "sidebar.workspace", apps: ["people"] },
];

export interface SidebarSection {
  id: string;
  labelKey: string;
  routes: ReadonlyArray<Route>;
}

const NAV_ORDER: ReadonlyArray<Route> = [...sidebarPrimary, ...sidebarWorkspace];

export const sidebarSections: ReadonlyArray<SidebarSection> = SIDEBAR_SECTION_CONFIG.map((g) => ({
  id: g.id,
  labelKey: g.labelKey,
  routes: NAV_ORDER.filter((r) => g.apps.includes(r.id)),
}));

/** Mobile bottom navigation (design: BOTTOM_NAV) — Hub, Email, Chat, Planner, then More. */
export const bottomNavRoutes: ReadonlyArray<Route> = [dashboard, email, chat, planner];

export function getRouteById(id: string): Route | undefined {
  return routes.find((r) => r.id === id);
}
